import { useParams, Link } from "react-router-dom";
import NotFoundPage from "@/pages/NotFoundPage";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Calendar, UserCheck, ShieldCheck, ArrowRight, ArrowUpRight, BookOpen, Loader2, Bookmark } from "lucide-react";
import { formatarData } from "@/data/artigosData";
import { PROFISSIONAIS_EXEMPLO, CATEGORIA_COR, COR_CLASSES, getInitials } from "@/data/profissionaisData";
import Seo from "@/components/Seo";
import { getJSON, accountSend } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const ArtigoPage = () => {
  const { slug } = useParams();
  const [artigo, setArtigo] = useState(undefined);
  const { user, setUser } = useAuth();
  const isFamilia = user && user.role === "familia";
  const isSaved = isFamilia && (user.favoritos || []).includes(slug);

  const toggleSave = async () => {
    try {
      const r = await accountSend("/account/favorites", "POST", { slug });
      setUser({ ...user, favoritos: r.favoritos });
      toast.success(r.favoritos.includes(slug) ? "Salvo em 'Salvos'." : "Removido dos salvos.");
    } catch (e) { toast.error(e.message); }
  };

  useEffect(() => {
    setArtigo(undefined);
    getJSON(`/conteudos/${slug}`).then(setArtigo).catch(() => setArtigo(null));
  }, [slug]);

  if (artigo === undefined) {
    return <main data-testid="artigo-loading" className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-maternnia-eucalipto" size={28} /></main>;
  }
  if (artigo === null) return <NotFoundPage />;

  const relacionados = artigo.relacionados || [];
  const profs = PROFISSIONAIS_EXEMPLO.filter((p) => (artigo.profissionais || []).includes(p.id));
  const paragrafos = (artigo.corpo || "").split(/\n{2,}/).map((s) => s.trim()).filter(Boolean);
  const refs = (artigo.referencias || "").split(/\n+/).map((s) => s.trim()).filter(Boolean);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: artigo.titulo,
    description: artigo.resumo,
    image: artigo.imagem,
    datePublished: artigo.data,
    author: { "@type": "Organization", name: artigo.autor || "Maternnia" },
    publisher: {
      "@type": "Organization",
      name: "Maternnia",
      logo: {
        "@type": "ImageObject",
        url: (typeof window !== "undefined" ? window.location.origin : "") + "/maternnia-logo-dark.png",
      },
    },
    articleSection: artigo.etapa,
  };

  return (
    <main data-testid="artigo-page" className="bg-white">
      <Seo
        title={artigo.titulo}
        path={`/conteudos/${artigo.slug}`}
        description={artigo.resumo}
        image={artigo.imagem}
        type="article"
        jsonLd={articleJsonLd}
      />
      {/* HERO */}
      <article>
        <header className="max-w-3xl mx-auto px-6 md:px-12 pt-10 md:pt-14">
          <Link to="/conteudos" data-testid="artigo-voltar" className="inline-flex items-center gap-2 text-sm text-maternnia-taupe hover:text-maternnia-charcoal transition-colors mb-8">
            <ArrowLeft size={16} /> Voltar aos conteúdos
          </Link>
          <div className="flex flex-wrap items-center gap-2 mb-5">
            <span className="text-xs font-semibold tracking-wide uppercase bg-maternnia-porcelana text-maternnia-charcoal px-3 py-1.5 rounded-full">{artigo.etapa}</span>
            <span className="text-xs font-semibold tracking-wide uppercase bg-maternnia-pink/10 text-maternnia-pink px-3 py-1.5 rounded-full">{artigo.categoria}</span>
          </div>
          <h1 data-testid="artigo-titulo" className="font-serif text-4xl md:text-5xl leading-[1.1] tracking-tight text-maternnia-charcoal font-medium">
            {artigo.titulo}
          </h1>
          <p className="mt-5 text-lg md:text-xl text-maternnia-taupe leading-relaxed font-light">{artigo.resumo}</p>

          <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-maternnia-taupe border-y border-maternnia-nevoa/60 py-4">
            <span className="inline-flex items-center gap-2"><BookOpen size={15} /> {artigo.autor}</span>
            {artigo.revisor && <span data-testid="artigo-revisor" className="inline-flex items-center gap-2 text-maternnia-eucalipto"><UserCheck size={15} /> Revisado por {artigo.revisor}</span>}
            <span className="inline-flex items-center gap-2"><Calendar size={15} /> {artigo.data ? formatarData(artigo.data) : ""}</span>
            <span className="inline-flex items-center gap-2"><Clock size={15} /> {artigo.tempoLeitura}</span>
            {isFamilia && (
              <button data-testid="artigo-salvar" onClick={toggleSave}
                className={`ml-auto inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-all ${isSaved ? "bg-maternnia-eucalipto text-white" : "border border-maternnia-nevoa text-maternnia-charcoal hover:bg-maternnia-porcelana/60"}`}>
                <Bookmark size={15} className={isSaved ? "fill-white" : ""} /> {isSaved ? "Salvo" : "Salvar"}
              </button>
            )}
          </div>
        </header>

        <motion.div
          className="max-w-4xl mx-auto px-6 md:px-12 mt-10"
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className={`rounded-[2rem] overflow-hidden shadow-[0_24px_64px_rgba(43,37,38,0.12)] ${artigo.imgPortrait ? "aspect-[4/5] max-w-sm mx-auto" : "aspect-[16/9]"}`}>
            <img src={artigo.imagem} alt={artigo.imagemAlt} className={`w-full h-full object-cover ${artigo.imgPos || ""}`} />
          </div>
        </motion.div>

        {/* BODY */}
        <div data-testid="artigo-corpo" className="max-w-[680px] mx-auto px-6 md:px-0 mt-12 md:mt-16">
          {paragrafos.map((p, i) => (
            <p key={i} className="text-[17px] md:text-lg text-maternnia-charcoal/85 leading-[1.8] mb-6 font-light">{p}</p>
          ))}

          {/* AVISO DE SAÚDE */}
          <div data-testid="artigo-aviso-saude" className="bg-maternnia-nevoa/40 rounded-2xl p-6 my-10 flex gap-3">
            <ShieldCheck size={20} className="text-maternnia-eucalipto shrink-0 mt-0.5" />
            <p className="text-sm text-maternnia-taupe leading-relaxed font-light">
              Este conteúdo tem caráter educativo e não substitui consulta, diagnóstico ou acompanhamento individual com profissionais de saúde.
            </p>
          </div>

          {/* REFERÊNCIAS */}
          {refs.length > 0 && (
            <div data-testid="artigo-referencias" className="my-10">
              <h3 className="font-serif text-xl text-maternnia-charcoal font-medium mb-3">Referências</h3>
              <ol className="list-decimal list-inside space-y-2 text-sm text-maternnia-taupe font-light">
                {refs.map((r, i) => <li key={i}>{r}</li>)}
              </ol>
            </div>
          )}
        </div>
      </article>

      {/* PROFISSIONAIS RELACIONADOS */}
      {profs.length > 0 && (
        <section data-testid="artigo-profissionais" className="bg-maternnia-porcelana/50 py-16 mt-6">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <h2 className="font-serif text-2xl md:text-3xl text-maternnia-charcoal font-medium mb-8">Profissionais relacionados</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {profs.map((p) => {
                const cor = COR_CLASSES[CATEGORIA_COR[p.categoria]] || COR_CLASSES.pink;
                return (
                  <Link key={p.id} to={`/profissionais/${p.id}`} data-testid={`artigo-prof-${p.id}`}
                    className="group flex items-center gap-4 bg-white rounded-2xl p-5 border border-maternnia-nevoa/60 hover:-translate-y-0.5 hover:shadow-md transition-all duration-300">
                    <span className={`h-12 w-12 rounded-2xl ${cor.bg} ${cor.text} flex items-center justify-center font-serif text-lg font-medium shrink-0`}>{getInitials(p.nome)}</span>
                    <div className="min-w-0">
                      <p className="font-medium text-maternnia-charcoal truncate">{p.nome}</p>
                      <p className={`text-xs ${cor.text}`}>{p.categoria}</p>
                    </div>
                    <ArrowUpRight size={16} className="ml-auto text-maternnia-taupe transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CONTEÚDOS RELACIONADOS */}
      {relacionados.length > 0 && (
        <section data-testid="artigo-relacionados" className="max-w-7xl mx-auto px-6 md:px-12 py-16">
          <h2 className="font-serif text-2xl md:text-3xl text-maternnia-charcoal font-medium mb-8">Continue lendo</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
            {relacionados.map((a) => (
              <Link key={a.id} to={`/conteudos/${a.slug}`} data-testid={`relacionado-${a.id}`}
                className="group block bg-white rounded-3xl overflow-hidden border border-maternnia-nevoa/60 shadow-sm hover:shadow-[0_16px_40px_rgba(43,37,38,0.08)] hover:-translate-y-1 transition-all duration-300">
                <div className={`overflow-hidden ${a.imgPortrait ? "aspect-[4/5]" : "aspect-[4/3]"}`}>
                  <img src={a.imagem} alt={a.imagemAlt} loading="lazy" className={`w-full h-full object-cover ${a.imgPos || ""} group-hover:scale-105 transition-transform duration-500`} />
                </div>
                <div className="p-6">
                  <span className="text-xs font-semibold tracking-wide text-maternnia-eucalipto uppercase">{a.categoria}</span>
                  <h3 className="font-serif text-xl text-maternnia-charcoal mt-2 leading-snug font-medium group-hover:text-maternnia-eucalipto transition-colors">{a.titulo}</h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* CTA CADASTRO */}
      <section className="bg-maternnia-eucalipto">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-20 text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white font-medium tracking-tight">Receba conteúdos para a sua etapa</h2>
          <p className="mt-4 text-white/85 font-light max-w-xl mx-auto">Cadastre-se gratuitamente e receba materiais selecionados para o seu momento na jornada.</p>
          <a href="/#cadastro" data-testid="artigo-cta-cadastro"
            className="group mt-8 inline-flex items-center gap-2 bg-white text-maternnia-charcoal hover:bg-maternnia-porcelana rounded-full px-8 py-4 text-base font-medium transition-all duration-300 hover:scale-[1.03] active:scale-[0.98]">
            Quero receber gratuitamente
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </div>
      </section>
    </main>
  );
};

export default ArtigoPage;
